/**
 * Terminal API Client
 *
 * Handles all API communication for the terminal portfolio explorer.
 * Used by both web terminal and CLI tool.
 */

import type {
  Entry,
  Commit,
  ITerminalApiClient,
  SkillsData,
  EasterEggData,
} from './types'

/**
 * Terminal API Client Implementation
 *
 * @example
 * ```ts
 * const client = new TerminalApiClient('https://agustinmusi.com')
 * const entries = await client.ls('/projects')
 * const content = await client.cat('/about.md')
 * ```
 */
export class TerminalApiClient implements ITerminalApiClient {
  /** Base URL for API requests (empty string for relative URLs in browser) */
  private baseUrl: string

  /** In-memory cache for API responses (5-minute TTL) */
  private cache = new Map<string, { data: any; expires: number }>()

  /** Cache TTL in milliseconds (5 minutes) */
  private cacheTTL = 5 * 60 * 1000

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl
  }

  /**
   * List directory contents
   */
  async ls(path: string): Promise<Entry[]> {
    const cacheKey = `ls:${path}`
    const cached = this.getFromCache<Entry[]>(cacheKey)
    if (cached) return cached

    const response = await fetch(`${this.baseUrl}/api/terminal/fs${path}`)
    if (!response.ok) {
      throw new Error(`Failed to list directory: ${response.statusText}`)
    }

    const data = (await response.json()) as { type: string; entries: Entry[] }

    // Expecting { type: 'directory', entries: Entry[] }
    if (data.type !== 'directory') {
      throw new Error(`Not a directory: ${path}`)
    }

    this.setCache(cacheKey, data.entries)
    return data.entries
  }

  /**
   * Read file contents
   */
  async cat(path: string): Promise<string> {
    const cacheKey = `cat:${path}`
    const cached = this.getFromCache<string>(cacheKey)
    if (cached) return cached

    const response = await fetch(`${this.baseUrl}/api/terminal/fs${path}`)
    if (!response.ok) {
      throw new Error(`Failed to read file: ${response.statusText}`)
    }

    const data = (await response.json()) as { type: string; contents: string }

    // Expecting { type: 'file', contents: string }
    if (data.type !== 'file') {
      throw new Error(`Not a file: ${path}`)
    }

    this.setCache(cacheKey, data.contents)
    return data.contents
  }

  /**
   * Get all portfolio projects
   */
  async getProjects(): Promise<any[]> {
    const cacheKey = 'projects'
    const cached = this.getFromCache<any[]>(cacheKey)
    if (cached) return cached

    const response = await fetch(`${this.baseUrl}/api/projects`)
    if (!response.ok) {
      throw new Error(`Failed to fetch projects: ${response.statusText}`)
    }

    const projects = (await response.json()) as any[]
    this.setCache(cacheKey, projects)
    return projects
  }

  /**
   * Get commits for a specific repository
   */
  async getRepoCommits(repoId: string): Promise<Commit[]> {
    const cacheKey = `commits:${repoId}`
    const cached = this.getFromCache<Commit[]>(cacheKey)
    if (cached) return cached

    const response = await fetch(
      `${this.baseUrl}/api/repositories/${repoId}`,
    )
    if (!response.ok) {
      throw new Error(
        `Failed to fetch repository commits: ${response.statusText}`,
      )
    }

    const data = (await response.json()) as { commits?: Commit[] }

    // Extract commits from repository data
    const commits: Commit[] = data.commits || []

    this.setCache(cacheKey, commits)
    return commits
  }

  /**
   * Get all GitHub repositories
   */
  async getRepositories(): Promise<any[]> {
    const cacheKey = 'repositories'
    const cached = this.getFromCache<any[]>(cacheKey)
    if (cached) return cached

    const response = await fetch(`${this.baseUrl}/api/repositories`)
    if (!response.ok) {
      throw new Error(
        `Failed to fetch repositories: ${response.statusText}`,
      )
    }

    const repos = (await response.json()) as any[]
    this.setCache(cacheKey, repos)
    return repos
  }

  /**
   * Get easter egg data (whoami, fortune, history)
   */
  async getEasterEgg(command: string): Promise<any> {
    const cacheKey = `easter-egg:${command}`
    const cached = this.getFromCache(cacheKey)
    if (cached) return cached

    const response = await fetch(
      `${this.baseUrl}/api/terminal/easter-eggs`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch easter eggs: ${response.statusText}`)
    }

    const data: EasterEggData = await response.json()

    // Extract the specific command data
    let result: any

    switch (command) {
      case 'whoami':
        result = data.whoami
        break
      case 'fortune':
        result = data.fortune
        break
      case 'history':
        result = data.history
        break
      default:
        result = null
    }

    this.setCache(cacheKey, result)
    return result
  }

  /**
   * Get skills data
   */
  async getSkills(): Promise<SkillsData> {
    const cacheKey = 'skills'
    const cached = this.getFromCache<SkillsData>(cacheKey)
    if (cached) return cached

    const response = await fetch(
      `${this.baseUrl}/api/terminal/content/skills`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch skills: ${response.statusText}`)
    }

    const skills = (await response.json()) as SkillsData
    this.setCache(cacheKey, skills)
    return skills
  }

  /**
   * Get data from cache if not expired
   */
  private getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key)
    if (!cached) return null

    if (Date.now() > cached.expires) {
      this.cache.delete(key)
      return null
    }

    return cached.data as T
  }

  /**
   * Store data in cache with TTL
   */
  private setCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      expires: Date.now() + this.cacheTTL,
    })
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear()
  }
}
